import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout.jsx';
import InvitationsAPI from '../../api/admin/invitations.js';
import './AdminUsers.css';

const AdminUsers = () => {
  const [activeTab, setActiveTab] = useState('invitations');
  const [invitations, setInvitations] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);

  // Invitation form state
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    clinic_id: '',
    email: '',
    name: '',
    role: 'practitioner',
    personal_message: ''
  });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState('');

  const loadData = async () => {
    try {
      setLoading(true);

      // Load invitations and clinics in parallel
      const [invitationsResult, clinicsResult] = await Promise.all([
        InvitationsAPI.getInvitations(),
        InvitationsAPI.getClinics()
      ]);

      if (invitationsResult.success) {
        setInvitations(invitationsResult.data.invitations || []);
      }

      if (clinicsResult.success) {
        setClinics(clinicsResult.data.clinics || []);
        // Set default clinic if none selected
        if (clinicsResult.data.clinics?.length > 0 && !inviteForm.clinic_id) {
          setInviteForm(prev => ({ ...prev, clinic_id: clinicsResult.data.clinics[0].id }));
        }
      }

    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInviteSubmit = async (e) => {
    e.preventDefault();
    setInviteLoading(true);
    setInviteError('');

    try {
      const result = await InvitationsAPI.sendInvitation(inviteForm);
      
      if (result.success) {
        // Close form and refresh invitations
        setShowInviteForm(false);
        setInviteForm({
          clinic_id: clinics[0]?.id || '',
          email: '',
          name: '',
          role: 'practitioner',
          personal_message: ''
        });
        await loadData();
      } else {
        setInviteError(result.error);
      }
    } catch (err) {
      setInviteError('Failed to send invitation');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    if (!window.confirm('Are you sure you want to cancel this invitation?')) {
      return;
    }

    try {
      const result = await InvitationsAPI.cancelInvitation(invitationId);
      if (result.success) {
        await loadData();
      } else {
        alert('Failed to cancel invitation: ' + result.error);
      }
    } catch (err) {
      alert('Failed to cancel invitation');
    }
  };

  const handleResendInvitation = async (invitationId) => {
    try {
      const result = await InvitationsAPI.resendInvitation(invitationId);
      if (result.success) {
        alert('Invitation resent successfully!');
        await loadData();
      } else {
        alert('Failed to resend invitation: ' + result.error);
      }
    } catch (err) {
      alert('Failed to resend invitation');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'orange';
      case 'completed': return 'green';
      case 'expired': return 'red';
      case 'cancelled': return 'gray';
      default: return 'blue';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-users-loading">
          <div className="loading-spinner"></div>
          <p>Loading user management...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-users">
        <div className="page-header">
          <h1>User Management</h1>
          <p>Invite and manage users across all clinics</p>
          
          <div className="header-actions">
            <button 
              className="btn btn-primary"
              onClick={() => setShowInviteForm(true)}
            >
              <i className="fas fa-user-plus"></i>
              Send Invitation
            </button>
          </div>
        </div>

        <div className="tabs">
          <button 
            className={`tab ${activeTab === 'invitations' ? 'active' : ''}`}
            onClick={() => setActiveTab('invitations')}
          >
            <i className="fas fa-envelope"></i>
            Invitations ({invitations.length})
          </button>
          <button 
            className={`tab ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            <i className="fas fa-users"></i>
            Users (Coming Soon)
          </button>
        </div>

        {activeTab === 'invitations' && (
          <div className="invitations-section">
            <div className="section-header">
              <h2>Pending & Recent Invitations</h2>
              <button 
                className="btn btn-secondary"
                onClick={loadData}
              >
                <i className="fas fa-refresh"></i>
                Refresh
              </button>
            </div>

            {invitations.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-envelope-open-text"></i>
                <h3>No invitations yet</h3>
                <p>Send your first invitation to get started</p>
                <button 
                  className="btn btn-primary"
                  onClick={() => setShowInviteForm(true)}
                >
                  Send Invitation
                </button>
              </div>
            ) : (
              <div className="invitations-table">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Clinic</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Sent</th>
                      <th>Expires</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invitations.map(invitation => (
                      <tr key={invitation.id}>
                        <td className="name-cell" data-label="Name">{invitation.name}</td>
                        <td className="email-cell" data-label="Email">{invitation.email}</td>
                        <td data-label="Clinic">{invitation.clinic_name}</td>
                        <td data-label="Role">
                          <span className={`role-badge ${invitation.role}`}>
                            {invitation.role}
                          </span>
                        </td>
                        <td data-label="Status">
                          <span 
                            className={`status-badge ${invitation.status}`}
                            style={{ backgroundColor: getStatusColor(invitation.status) }}
                          >
                            {invitation.status}
                          </span>
                        </td>
                        <td className="date-cell" data-label="Sent">
                          {formatDate(invitation.created_at)}
                        </td>
                        <td className="date-cell" data-label="Expires">
                          {formatDate(invitation.expires_at)}
                        </td>
                        <td className="actions-cell" data-label="Actions">
                          {invitation.status === 'pending' && (
                            <div className="action-buttons">
                              <button 
                                className="btn btn-small btn-secondary"
                                onClick={() => handleResendInvitation(invitation.id)}
                                title="Resend invitation email to user"
                              >
                                <i className="fas fa-paper-plane"></i>
                              </button>
                              <button 
                                className="btn btn-small btn-danger"
                                onClick={() => handleCancelInvitation(invitation.id)}
                                title="Cancel invitation and prevent signup"
                              >
                                <i className="fas fa-times"></i>
                              </button>
                            </div>
                          )}
                          {invitation.status === 'completed' && (
                            <span className="completed-text">
                              <i className="fas fa-check"></i>
                              Completed
                            </span>
                          )}
                          {invitation.status === 'expired' && (
                            <button 
                              className="btn btn-small btn-secondary"
                              onClick={() => handleResendInvitation(invitation.id)}
                              title="Send new invitation (original has expired)"
                            >
                              <i className="fas fa-redo"></i>
                            </button>
                          )}
                          {invitation.status === 'cancelled' && (
                            <span className="cancelled-text" title="Invitation was cancelled by admin">
                              <i className="fas fa-ban"></i>
                              Cancelled
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'users' && (
          <div className="users-section">
            <div className="placeholder-notice">
              <h3>👥 User Management Coming Soon</h3>
              <p>Direct user management features will be available in the next release.</p>
              <p>For now, use the invitation system to add new users to clinics.</p>
            </div>
          </div>
        )}

        {/* Invitation Form Modal */}
        {showInviteForm && (
          <div className="modal-overlay" onClick={() => setShowInviteForm(false)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Send User Invitation</h2>
                <button 
                  className="close-btn"
                  onClick={() => setShowInviteForm(false)}
                  title="Close invitation form"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              
              <form onSubmit={handleInviteSubmit} className="invite-form">
                {inviteError && (
                  <div className="alert alert-error">
                    <i className="fas fa-exclamation-triangle"></i>
                    {inviteError}
                  </div>
                )}

                <div className="form-group">
                  <label>Clinic *</label>
                  <select 
                    value={inviteForm.clinic_id}
                    onChange={(e) => setInviteForm(prev => ({ ...prev, clinic_id: e.target.value }))}
                    required
                  >
                    <option value="">Select a clinic</option>
                    {clinics.map(clinic => (
                      <option key={clinic.id} value={clinic.id}>
                        {clinic.name} ({clinic.user_count} users)
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Full Name *</label>
                    <input
                      type="text"
                      value={inviteForm.name}
                      onChange={(e) => setInviteForm(prev => ({ ...prev, name: e.target.value }))}
                      required
                      placeholder="Enter full name"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Role *</label>
                    <select 
                      value={inviteForm.role}
                      onChange={(e) => setInviteForm(prev => ({ ...prev, role: e.target.value }))}
                      required
                    >
                      <option value="practitioner">Practitioner</option>
                      <option value="staff">Staff</option>
                      <option value="admin">Admin</option>
                      <option value="readonly">Read Only</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Email Address *</label>
                  <input
                    type="email"
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm(prev => ({ ...prev, email: e.target.value }))}
                    required
                    placeholder="Enter email address"
                  />
                </div>

                <div className="form-group">
                  <label>Personal Message (Optional)</label>
                  <textarea
                    value={inviteForm.personal_message}
                    onChange={(e) => setInviteForm(prev => ({ ...prev, personal_message: e.target.value }))}
                    placeholder="Add a personal welcome message..."
                    rows={3}
                  />
                </div>

                <div className="form-actions">
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={() => setShowInviteForm(false)}
                    disabled={inviteLoading}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={inviteLoading}
                  >
                    {inviteLoading ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i>
                        Sending...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-paper-plane"></i>
                        Send Invitation
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminUsers;